import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  BadgeCheck,
  Briefcase,
  FileText,
  GraduationCap,
  User,
  Video,
  MessageSquare,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { isSectionVisible, isSectionVisibleWithData } from "../utils";
import {
  Section,
  SocialLinks,
  ExperienceList,
  EducationList,
  SkillsGrid,
  ProjectsGrid,
  TestimonialsSection,
  ReelsSection,
  BusinessCardSection,
  // New extracted components
  HeaderSection,
  HeroSection,
  CertificationsSection,
  AwardsSection,
  ServicesSection,
  BlogSection,
  VolunteerSection,
  PublicationsSection,
  ContactSection,
  BottomNavigation,
  FooterSection,
} from "../components";
import {
  getPublicProfile,
  transformProfileData,
} from "@/api/methods/publicProfile";
import { TransformedProfileData } from "@/types/publicProfile";

function useQueryParam(param: string) {
  return useMemo(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get(param);
  }, [param]);
}

const PublicProfilePage: React.FC = () => {
  const [profileData, setProfileData] = useState<TransformedProfileData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>("about");
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navigationRef = useRef<HTMLDivElement>(null);

  const profileId = useQueryParam("share");
  const cardId = useQueryParam("cardId"); // Optional card ID for specific privacy settings
  const isPreview = useQueryParam("preview") === "true"; // Check for preview mode

  useEffect(() => {
    const fetchProfile = async () => {
      if (!profileId) {
        setError("Profile ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const apiResponse = await getPublicProfile(profileId, isPreview);
        const transformedData = transformProfileData(
          apiResponse,
          cardId || undefined
        );
        setProfileData(transformedData);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError(err instanceof Error ? err.message : "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [profileId, cardId, isPreview]);

  useEffect(() => {
    if (!profileData) return;

    // Update page title
    const originalTitle = document.title;
    document.title = `${profileData.personalInfo.name} - Portfolio | Connectifyz`;

    // Update favicon with user's profile image
    const updateFavicon = () => {
      // Remove existing favicons
      const existingIcons = document.querySelectorAll('link[rel*="icon"]');
      existingIcons.forEach((icon) => icon.remove());

      // Create new favicon with user's avatar
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      canvas.width = 32;
      canvas.height = 32;

      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (ctx) {
          // Draw circular avatar
          ctx.clearRect(0, 0, 32, 32);
          ctx.beginPath();
          ctx.arc(16, 16, 16, 0, 2 * Math.PI);
          ctx.closePath();
          ctx.clip();

          // Draw the image
          ctx.drawImage(img, 0, 0, 32, 32);

          // Convert to favicon
          const faviconUrl = canvas.toDataURL("image/png");

          // Create and append favicon
          const favicon = document.createElement("link");
          favicon.rel = "icon";
          favicon.type = "image/png";
          favicon.href = faviconUrl;
          document.head.appendChild(favicon);

          // Also create apple touch icon
          const appleTouchIcon = document.createElement("link");
          appleTouchIcon.rel = "apple-touch-icon";
          appleTouchIcon.href = faviconUrl;
          document.head.appendChild(appleTouchIcon);
        }
      };

      img.onerror = () => {
        // Fallback to default app icon if avatar fails to load
        const favicon = document.createElement("link");
        favicon.rel = "icon";
        favicon.type = "image/x-icon";
        favicon.href = "/favicon.ico";
        document.head.appendChild(favicon);
      };

      img.src = profileData.personalInfo.avatar;
    };

    updateFavicon();

    // Cleanup function to restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, [profileData]);

  const share = useMemo(
    () => async () => {
      if (!profileData) return;
      try {
        if (navigator.share) {
          await navigator.share({
            title: profileData.personalInfo.name,
            text: profileData.personalInfo.headline,
            url: window.location.href,
          });
        } else {
          await navigator.clipboard.writeText(window.location.href);
          alert("Profile link copied to clipboard");
        }
      } catch {
        // ignore
      }
    },
    [profileData]
  );

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Header auto-hide logic
      if (currentScrollY > 100) {
        if (currentScrollY > lastScrollY && isHeaderVisible) {
          // Scrolling down - hide header
          setIsHeaderVisible(false);
        } else if (currentScrollY < lastScrollY && !isHeaderVisible) {
          // Scrolling up - show header
          setIsHeaderVisible(true);
        }
      } else {
        // Always show header when near top
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);

      // Active section detection
      const sections = document.querySelectorAll("section[id]");
      const scrollPosition = currentScrollY + window.innerHeight / 2;

      for (const section of sections) {
        const element = section as HTMLElement;
        const { offsetTop, offsetHeight } = element;

        if (
          scrollPosition >= offsetTop &&
          scrollPosition < offsetTop + offsetHeight
        ) {
          setActiveSection(element.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Call once to set initial state

    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, isHeaderVisible]);

  useEffect(() => {
    if (!navigationRef.current) return;

    const scrollActiveTabToCenter = () => {
      const activeTab = navigationRef.current!.querySelector(
        `[href="#${activeSection}"]`
      ) as HTMLAnchorElement;
      if (activeTab) {
        const container = navigationRef.current!;
        const containerWidth = container.clientWidth;
        const tabLeft = activeTab.offsetLeft;
        const tabWidth = activeTab.clientWidth;

        const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    };

    // Small delay to ensure DOM is updated
    setTimeout(scrollActiveTabToCenter, 100);
  }, [activeSection]);

  const navigationItems = useMemo(() => {
    if (!profileData) return [];
    return [
      {
        href: "#about",
        label: "About",
        visible: isSectionVisibleWithData(
          profileData.sections.about.privacy,
          undefined,
          profileData.sections.about.content
        ),
      },
      {
        href: "#experience",
        label: "Experience",
        visible: isSectionVisibleWithData(
          profileData.sections.experience.privacy,
          profileData.sections.experience.items
        ),
      },
      {
        href: "#education",
        label: "Education",
        visible: isSectionVisibleWithData(
          profileData.sections.education.privacy,
          profileData.sections.education.items
        ),
      },
      {
        href: "#skills",
        label: "Skills",
        visible: isSectionVisibleWithData(
          profileData.sections.skills.privacy,
          profileData.sections.skills.items
        ),
      },
      {
        href: "#certifications",
        label: "Certifications",
        visible: isSectionVisibleWithData(
          profileData.sections.certifications.privacy,
          profileData.sections.certifications.items
        ),
      },
      {
        href: "#awards",
        label: "Awards",
        visible: isSectionVisibleWithData(
          profileData.sections.awards.privacy,
          profileData.sections.awards.items
        ),
      },
      {
        href: "#services",
        label: "Services",
        visible: isSectionVisibleWithData(
          profileData.sections.services.privacy,
          profileData.sections.services.items
        ),
      },
      {
        href: "#projects",
        label: "Projects",
        visible: isSectionVisibleWithData(
          profileData.sections.projects.privacy,
          profileData.sections.projects.items
        ),
      },
      {
        href: "#testimonials",
        label: "Testimonials",
        visible: isSectionVisibleWithData(
          profileData.sections.testimonials.privacy,
          profileData.sections.testimonials.items
        ),
      },
      {
        href: "#blog",
        label: "Blog",
        visible: isSectionVisibleWithData(
          profileData.sections.blog.privacy,
          profileData.sections.blog.items
        ),
      },
      {
        href: "#volunteer",
        label: "Volunteer",
        visible: isSectionVisibleWithData(
          profileData.sections.volunteer.privacy,
          profileData.sections.volunteer.items
        ),
      },
      {
        href: "#publications",
        label: "Publications",
        visible: isSectionVisibleWithData(
          profileData.sections.publications.privacy,
          profileData.sections.publications.items
        ),
      },
      {
        href: "#reels",
        label: "Reels",
        visible: isSectionVisibleWithData(
          profileData.sections.reels.privacy,
          profileData.sections.reels.items
        ),
      },
      {
        href: "#card",
        label: "Card",
        visible: isSectionVisible(profileData.sections.businessCard.privacy),
      },
      { href: "#contact", label: "Contact", visible: true },
    ].filter((item) => item.visible);
  }, [profileData]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-dvh bg-white text-black dark:bg-black dark:text-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm text-black/70 dark:text-white/70">
            Loading profile...
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !profileData) {
    return (
      <div className="min-h-dvh bg-white text-black dark:bg-black dark:text-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <h1 className="text-xl font-semibold">Profile not found</h1>
          <p className="text-sm text-black/70 dark:text-white/70">
            {error ||
              "The profile you're looking for doesn't exist or is not publicly available."}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-apple-primary px-4 py-2 text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Use the fetched data
  const data = profileData;

  function downloadVCard() {
    const personalInfo = data?.personalInfo;
    const contactInfo = data?.contactInfo;

    if (!personalInfo?.name) return;

    const [firstName, ...rest] = personalInfo.name.split(" ");
    const lastName = rest.join(" ");

    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `N:${lastName};${firstName};;;`,
      `FN:${personalInfo.name}`,
      personalInfo.headline ? `TITLE:${personalInfo.headline}` : "",
      contactInfo?.email ? `EMAIL;TYPE=INTERNET:${contactInfo.email}` : "",
      personalInfo.email ? `EMAIL;TYPE=WORK:${personalInfo.email}` : "",
      contactInfo?.phone ? `TEL;TYPE=CELL:${contactInfo.phone}` : "",
      personalInfo.phone ? `TEL;TYPE=WORK:${personalInfo.phone}` : "",
      contactInfo?.website ? `URL:${contactInfo.website}` : "",
      personalInfo.website ? `URL;TYPE=WORK:${personalInfo.website}` : "",
      contactInfo?.location ? `ADR;TYPE=HOME:;;${contactInfo.location};;;` : "",
      personalInfo.location
        ? `ADR;TYPE=WORK:;;${personalInfo.location};;;`
        : "",
      personalInfo.bio ? `NOTE:${personalInfo.bio}` : "",
      personalInfo.timezone ? `TZ:${personalInfo.timezone}` : "",
      personalInfo.languages?.length > 0
        ? `LANG:${personalInfo.languages.join(",")}`
        : "",
      personalInfo.calendlyUrl
        ? `URL;TYPE=CALENDAR:${personalInfo.calendlyUrl}`
        : "",
      personalInfo.avatar ? `PHOTO;TYPE=JPEG:${personalInfo.avatar}` : "",
      "END:VCARD",
    ]
      .filter(Boolean)
      .join("\n");

    const blob = new Blob([vcard], { type: "text/vcard" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${firstName || "contact"}.vcf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  return (
    <div className="min-h-dvh bg-white text-black dark:bg-black dark:text-white">
      {/* Header Section - Auto-hiding */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-black/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 transition-transform duration-300 ease-in-out ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <HeaderSection
          profileData={data}
          onDownloadVCard={() => downloadVCard()}
          onShare={share}
        />
      </div>

      {/* Hero Section - Full Screen */}
      <HeroSection profileData={data} downloadVCard={downloadVCard} />

      {/* Content Sections - Scrollable */}
      <main className="space-y-16 py-16 bg-white dark:bg-gray-900">
        {/* About Section */}
        {isSectionVisibleWithData(
          data.sections.about.privacy,
          undefined,
          data.sections.about.content
        ) && (
          <Section id="about" title="About" icon={<User className="h-5 w-5" />}>
            <div className="prose-aesthetic text-pretty text-secondary md:text-lg">
              {data.sections.about.content}
            </div>
            {isSectionVisible(data.sections.socials.privacy) && (
              <SocialLinks socials={data.sections.socials.items} />
            )}
          </Section>
        )}

        {/* Reels Section */}
        {isSectionVisibleWithData(
          data.sections.reels.privacy,
          data.sections.reels.items
        ) && (
          <div className="band-apple py-10">
            <Section
              id="reels"
              title="Reels"
              icon={<Video className="h-5 w-5" />}
            >
              <ReelsSection reels={data.sections.reels.items as any} />
            </Section>
          </div>
        )}

        {/* Experience Section */}
        {isSectionVisibleWithData(
          data.sections.experience.privacy,
          data.sections.experience.items
        ) && (
          <Section
            id="experience"
            title="Experience"
            icon={<Briefcase className="h-5 w-5" />}
          >
            <ExperienceList
              experiences={data.sections.experience.items as any}
            />
          </Section>
        )}

        {/* Education Section */}
        {isSectionVisibleWithData(
          data.sections.education.privacy,
          data.sections.education.items
        ) && (
          <Section
            id="education"
            title="Education"
            icon={<GraduationCap className="h-5 w-5" />}
          >
            <EducationList education={data.sections.education.items} />
          </Section>
        )}

        {/* Skills Section */}
        {isSectionVisibleWithData(
          data.sections.skills.privacy,
          data.sections.skills.items
        ) && (
          <Section
            id="skills"
            title="Skills"
            icon={<BadgeCheck className="h-5 w-5" />}
          >
            <SkillsGrid skills={data.sections.skills.items} />
          </Section>
        )}

        {/* Certifications Section */}
        {isSectionVisibleWithData(
          data.sections.certifications.privacy,
          data.sections.certifications.items
        ) && (
          <CertificationsSection
            certifications={data.sections.certifications.items}
          />
        )}

        {/* Awards Section */}
        {isSectionVisibleWithData(
          data.sections.awards.privacy,
          data.sections.awards.items
        ) && <AwardsSection awards={data.sections.awards.items} />}

        {/* Services Section */}
        {isSectionVisibleWithData(
          data.sections.services.privacy,
          data.sections.services.items
        ) && <ServicesSection services={data.sections.services.items} />}

        {/* Projects Section */}
        {isSectionVisibleWithData(
          data.sections.projects.privacy,
          data.sections.projects.items
        ) && (
          <Section
            id="projects"
            title="Projects"
            icon={<FileText className="h-5 w-5" />}
          >
            <ProjectsGrid projects={data.sections.projects.items} />
          </Section>
        )}

        {/* Testimonials Section */}
        {isSectionVisibleWithData(
          data.sections.testimonials.privacy,
          data.sections.testimonials.items
        ) && (
          <Section
            id="testimonials"
            title="Testimonials"
            icon={<MessageSquare className="h-5 w-5" />}
          >
            <TestimonialsSection
              testimonials={data.sections.testimonials.items}
            />
          </Section>
        )}

        {/* Blog Section */}
        {isSectionVisibleWithData(
          data.sections.blog.privacy,
          data.sections.blog.items
        ) && <BlogSection blogPosts={data.sections.blog.items} />}

        {/* Volunteer Section */}
        {isSectionVisibleWithData(
          data.sections.volunteer.privacy,
          data.sections.volunteer.items
        ) && <VolunteerSection volunteer={data.sections.volunteer.items} />}

        {/* Publications Section */}
        {isSectionVisibleWithData(
          data.sections.publications.privacy,
          data.sections.publications.items
        ) && (
          <PublicationsSection
            publications={data.sections.publications.items}
          />
        )}

        {/* Business Card Section */}
        {isSectionVisible(data.sections.businessCard.privacy) && (
          <Section
            id="card"
            title="Business Card"
            icon={<Briefcase className="h-5 w-5" />}
          >
            <BusinessCardSection
              businessCard={data.sections.businessCard.data as any}
              cardImages={data.cardImages}
            />
          </Section>
        )}

        {/* Contact Section */}
        <ContactSection
          profileData={data}
          onDownloadVCard={() => downloadVCard()}
          onShare={share}
        />
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation
        navigationItems={navigationItems}
        activeSection={activeSection}
        navigationRef={navigationRef}
      />

      {/* Footer */}
      <FooterSection profileData={data} />
    </div>
  );
};

export default PublicProfilePage;
